import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  Avatar,
  Button,
  Chip,
  Rating,
  Divider,
  Tab,
  Tabs,
  Card,
  CardContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  LinearProgress,
} from '@mui/material';
import {
  ArrowBack,
  Star,
  Verified,
  Schedule,
  TrendingUp,
  Chat,
  VideoCall,
  Gavel,
  WorkspacePremium,
  School,
  Business,
  Person,
  CheckCircle,
  ThumbUp,
  Language,
  Phone,
  Email,
  LocationOn,
} from '@mui/icons-material';
import { axelionColors } from '../../theme/axelionTheme';

// Mock lawyer data - matching the structure from LawyersPage
const MOCK_LAWYERS = {
  1: {
    id: 1,
    name: 'Иванов Иван Иванович',
    avatar: null,
    rating: 4.8,
    specializations: ['Гражданское право', 'Семейное право'],
    experience: 15,
    region: 'Ташкент',
    priceFrom: 50000,
    completedConsultations: 234,
    responseTime: 2,
    successRate: 96,
    verified: true,
    bio: 'Опытный юрист с 15-летним стажем работы в области гражданского и семейного права. Помог более 200 клиентам решить их юридические вопросы. Специализируюсь на семейных спорах, наследственных делах и договорном праве.',
    education: [
      'Ташкентский государственный юридический университет (2008)',
      'Магистратура по гражданскому праву (2010)',
    ],
    languages: ['Русский', 'Узбекский', 'Английский'],
    certifications: [
      'Сертифицированный медиатор',
      'Член Адвокатской палаты Узбекистана',
    ],
    phone: '+998 90 123-45-67',
    email: 'ivanov@maslaxat.uz',
    workingHours: 'Пн-Пт: 9:00-18:00, Сб: 10:00-15:00',
  },
  2: {
    id: 2,
    name: 'Петрова Мария Сергеевна',
    avatar: null,
    rating: 4.9,
    specializations: ['Корпоративное право', 'Налоговое право'],
    experience: 12,
    region: 'Ташкент',
    priceFrom: 75000,
    completedConsultations: 189,
    responseTime: 1,
    successRate: 98,
    verified: true,
    bio: 'Специалист в области корпоративного и налогового права. Работаю с крупными компаниями и стартапами. Помогаю оптимизировать налоговую нагрузку и структурировать бизнес.',
    education: [
      'МГУ им. Ломоносова, юридический факультет (2011)',
      'MBA в области корпоративного управления (2015)',
    ],
    languages: ['Русский', 'Английский', 'Немецкий'],
    certifications: [
      'Налоговый консультант',
      'Сертифицированный аудитор',
    ],
    phone: '+998 91 234-56-78',
    email: 'petrova@maslaxat.uz',
    workingHours: 'Пн-Пт: 10:00-19:00',
  },
  3: {
    id: 3,
    name: 'Сидоров Петр Александрович',
    avatar: null,
    rating: 4.7,
    specializations: ['Семейное право', 'Недвижимость'],
    experience: 10,
    region: 'Самарканд',
    priceFrom: 45000,
    completedConsultations: 156,
    responseTime: 3,
    successRate: 94,
    verified: true,
    bio: 'Практикующий юрист с опытом работы в семейном праве и сделках с недвижимостью. Специализируюсь на разводах, разделе имущества и оформлении купли-продажи недвижимости.',
    education: [
      'Самаркандский государственный университет (2013)',
    ],
    languages: ['Русский', 'Узбекский'],
    certifications: [
      'Риелтор-консультант',
    ],
    phone: '+998 93 345-67-89',
    email: 'sidorov@maslaxat.uz',
    workingHours: 'Пн-Сб: 9:00-17:00',
  },
};

// Mock reviews
const MOCK_REVIEWS = [
  {
    id: 1,
    clientName: 'Алексей К.',
    rating: 5,
    date: '15 ноября 2024',
    comment: 'Отличный специалист! Помог решить сложный семейный спор. Всё объяснил понятно и доступно. Рекомендую!',
  },
  {
    id: 2,
    clientName: 'Марина С.',
    rating: 5,
    date: '8 ноября 2024',
    comment: 'Профессионал своего дела. Быстро разобрался в ситуации и предложил оптимальное решение. Спасибо!',
  },
  {
    id: 3,
    clientName: 'Дмитрий П.',
    rating: 4,
    date: '2 ноября 2024',
    comment: 'Хороший юрист, но немного долго отвечал на вопросы. В остальном всё отлично.',
  },
];

const LawyerProfilePage = () => {
  const { lawyerId } = useParams();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);

  // Get lawyer data
  const lawyer = MOCK_LAWYERS[lawyerId] || MOCK_LAWYERS[1];

  const handleBookConsultation = () => {
    navigate('/consultations', { state: { lawyer } });
  };

  const handleStartChat = () => {
    navigate('/ai-chat', { state: { lawyerId: lawyer.id } });
  };

  const handleVideoCall = () => {
    navigate(`/consultations/video/${lawyer.id}`);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: axelionColors.bgCream, pb: 4 }}>
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${axelionColors.gold} 0%, ${axelionColors.goldDark} 100%)`,
          color: 'white',
          py: 3,
          px: 2,
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              color="inherit"
              onClick={() => navigate('/lawyers')}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 300,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
              }}
            >
              Профиль юриста
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Grid container spacing={3}>
          {/* Left Column - Lawyer Info */}
          <Grid item xs={12} md={4}>
            <Paper sx={{
              p: 3,
              borderRadius: '8px',
              border: `1px solid ${axelionColors.borderLight}`,
              boxShadow: 'none',
            }}>
              {/* Avatar and Name */}
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Avatar
                  sx={{
                    width: 120,
                    height: 120,
                    margin: '0 auto',
                    bgcolor: axelionColors.gold,
                    fontSize: '3rem',
                    mb: 2,
                    fontWeight: 300,
                  }}
                >
                  {lawyer.name.charAt(0)}
                </Avatar>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 300, letterSpacing: '0.05em' }}>
                    {lawyer.name}
                  </Typography>
                  {lawyer.verified && (
                    <Verified sx={{ color: axelionColors.gold, fontSize: 24 }} />
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 2 }}>
                  <Star sx={{ color: axelionColors.gold, fontSize: 20 }} />
                  <Typography variant="h6" fontWeight="600">
                    {lawyer.rating}
                  </Typography>
                  <Typography variant="body2" sx={{ color: axelionColors.textMuted }}>
                    ({lawyer.completedConsultations} консультаций)
                  </Typography>
                </Box>
                <Rating
                  value={lawyer.rating}
                  precision={0.1}
                  readOnly
                  sx={{
                    '& .MuiRating-iconFilled': { color: axelionColors.gold },
                    '& .MuiRating-iconEmpty': { color: axelionColors.goldMuted },
                  }}
                />
              </Box>

              <Divider sx={{ my: 2, borderColor: axelionColors.borderLight }} />

              {/* Quick Stats */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Schedule sx={{ color: axelionColors.gold }} />
                  <Typography variant="body2" sx={{ color: axelionColors.textSecondary }}>
                    Ответ через <strong>{lawyer.responseTime} часа</strong>
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <TrendingUp sx={{ color: axelionColors.gold }} />
                  <Typography variant="body2" sx={{ color: axelionColors.textSecondary }}>
                    <strong>{lawyer.successRate}%</strong> успешных дел
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <WorkspacePremium sx={{ color: axelionColors.gold }} />
                  <Typography variant="body2" sx={{ color: axelionColors.textSecondary }}>
                    <strong>{lawyer.experience} лет</strong> опыта
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationOn sx={{ color: axelionColors.gold }} />
                  <Typography variant="body2" sx={{ color: axelionColors.textSecondary }}>
                    {lawyer.region}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2, borderColor: axelionColors.borderLight }} />

              {/* Contact Info */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: axelionColors.textDark, mb: 1 }}>
                Контактная информация
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Phone sx={{ fontSize: 18, color: axelionColors.textMuted }} />
                  <Typography variant="body2" sx={{ color: axelionColors.textSecondary }}>{lawyer.phone}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Email sx={{ fontSize: 18, color: axelionColors.textMuted }} />
                  <Typography variant="body2" sx={{ color: axelionColors.textSecondary }}>{lawyer.email}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Schedule sx={{ fontSize: 18, color: axelionColors.textMuted }} />
                  <Typography variant="body2" sx={{ color: axelionColors.textSecondary }}>{lawyer.workingHours}</Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2, borderColor: axelionColors.borderLight }} />

              {/* Price */}
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="body2" sx={{ color: axelionColors.textMuted, mb: 0.5 }}>
                  Консультация от
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 300, color: axelionColors.gold }}>
                  {lawyer.priceFrom.toLocaleString()} сум
                </Typography>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  startIcon={<VideoCall />}
                  onClick={handleBookConsultation}
                  sx={{
                    py: 1.5,
                    background: `linear-gradient(135deg, ${axelionColors.gold} 0%, ${axelionColors.goldDark} 100%)`,
                    color: '#FFFFFF',
                    borderRadius: '8px',
                    fontWeight: 500,
                    letterSpacing: '0.05em',
                    textTransform: 'none',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${axelionColors.goldDark} 0%, ${axelionColors.bronze} 100%)`,
                    },
                  }}
                >
                  Записаться на консультацию
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  startIcon={<Chat />}
                  onClick={handleStartChat}
                  sx={{
                    py: 1.5,
                    borderRadius: '8px',
                    borderColor: axelionColors.gold,
                    color: axelionColors.gold,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: axelionColors.goldDark,
                      background: axelionColors.accentLight,
                    },
                  }}
                >
                  Начать чат
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  startIcon={<VideoCall />}
                  onClick={handleVideoCall}
                  sx={{
                    py: 1.5,
                    borderRadius: '8px',
                    borderColor: axelionColors.gold,
                    color: axelionColors.gold,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: axelionColors.goldDark,
                      background: axelionColors.accentLight,
                    },
                  }}
                >
                  Видеозвонок
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Right Column - Details */}
          <Grid item xs={12} md={8}>
            <Paper sx={{
              borderRadius: '8px',
              overflow: 'hidden',
              border: `1px solid ${axelionColors.borderLight}`,
              boxShadow: 'none',
            }}>
              {/* Tabs */}
              <Tabs
                value={currentTab}
                onChange={(e, val) => setCurrentTab(val)}
                sx={{
                  borderBottom: 1,
                  borderColor: axelionColors.borderLight,
                  bgcolor: axelionColors.bgWarm,
                  '& .MuiTab-root': {
                    color: axelionColors.textMuted,
                    textTransform: 'none',
                    fontWeight: 500,
                    '&.Mui-selected': {
                      color: axelionColors.gold,
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: axelionColors.gold,
                  },
                }}
              >
                <Tab label="О юристе" />
                <Tab label="Отзывы" />
                <Tab label="Портфолио" />
              </Tabs>

              <Box sx={{ p: 3 }}>
                {/* Tab 0: About */}
                {currentTab === 0 && (
                  <Box>
                    {/* Bio */}
                    <Typography variant="h6" sx={{ fontWeight: 600, color: axelionColors.textDark, mb: 1 }}>
                      О себе
                    </Typography>
                    <Typography variant="body1" sx={{ color: axelionColors.textSecondary, mb: 3 }}>
                      {lawyer.bio}
                    </Typography>

                    {/* Specializations */}
                    <Typography variant="h6" sx={{ fontWeight: 600, color: axelionColors.textDark, mt: 3, mb: 1 }}>
                      Специализации
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                      {lawyer.specializations.map((spec, index) => (
                        <Chip
                          key={index}
                          label={spec}
                          icon={<Gavel />}
                          sx={{
                            bgcolor: axelionColors.accentLight,
                            color: axelionColors.gold,
                            fontWeight: 600,
                            border: `1px solid ${axelionColors.gold}`,
                            borderRadius: '8px',
                            '& .MuiChip-icon': {
                              color: axelionColors.gold,
                            },
                          }}
                        />
                      ))}
                    </Box>

                    {/* Education */}
                    <Typography variant="h6" sx={{ fontWeight: 600, color: axelionColors.textDark, mt: 3, mb: 1 }}>
                      Образование
                    </Typography>
                    <List>
                      {lawyer.education.map((edu, index) => (
                        <ListItem key={index}>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: axelionColors.gold }}>
                              <School />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={edu}
                            primaryTypographyProps={{
                              sx: { color: axelionColors.textSecondary }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>

                    {/* Languages */}
                    <Typography variant="h6" sx={{ fontWeight: 600, color: axelionColors.textDark, mt: 3, mb: 1 }}>
                      Языки
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                      {lawyer.languages.map((lang, index) => (
                        <Chip
                          key={index}
                          label={lang}
                          icon={<Language />}
                          variant="outlined"
                          sx={{
                            borderColor: axelionColors.borderLight,
                            color: axelionColors.textSecondary,
                            borderRadius: '8px',
                          }}
                        />
                      ))}
                    </Box>

                    {/* Certifications */}
                    <Typography variant="h6" sx={{ fontWeight: 600, color: axelionColors.textDark, mt: 3, mb: 1 }}>
                      Сертификаты и достижения
                    </Typography>
                    <List>
                      {lawyer.certifications.map((cert, index) => (
                        <ListItem key={index}>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: axelionColors.bronze }}>
                              <WorkspacePremium />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={cert}
                            primaryTypographyProps={{
                              sx: { color: axelionColors.textSecondary }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Tab 1: Reviews */}
                {currentTab === 1 && (
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: axelionColors.textDark, mb: 2 }}>
                      Отзывы клиентов
                    </Typography>

                    {/* Rating Summary */}
                    <Card sx={{
                      mb: 3,
                      bgcolor: axelionColors.accentLight,
                      borderRadius: '8px',
                      border: `1px solid ${axelionColors.borderLight}`,
                      boxShadow: 'none',
                    }}>
                      <CardContent>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                            <Typography variant="h2" sx={{ fontWeight: 300, color: axelionColors.gold }}>
                              {lawyer.rating}
                            </Typography>
                            <Rating
                              value={lawyer.rating}
                              precision={0.1}
                              readOnly
                              size="large"
                              sx={{
                                '& .MuiRating-iconFilled': { color: axelionColors.gold },
                                '& .MuiRating-iconEmpty': { color: axelionColors.goldMuted },
                              }}
                            />
                            <Typography variant="body2" sx={{ color: axelionColors.textMuted }}>
                              На основе {lawyer.completedConsultations} отзывов
                            </Typography>
                          </Grid>
                          <Grid item xs={12} md={8}>
                            {[5, 4, 3, 2, 1].map((star) => (
                              <Box key={star} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="body2" sx={{ width: 60, color: axelionColors.textSecondary }}>
                                  {star} звезд
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={star === 5 ? 80 : star === 4 ? 15 : 5}
                                  sx={{
                                    flexGrow: 1,
                                    height: 8,
                                    borderRadius: '8px',
                                    bgcolor: axelionColors.bgBeige,
                                    '& .MuiLinearProgress-bar': {
                                      bgcolor: axelionColors.gold,
                                      borderRadius: '8px',
                                    },
                                  }}
                                />
                                <Typography variant="body2" sx={{ color: axelionColors.textMuted, width: 40 }}>
                                  {star === 5 ? '80%' : star === 4 ? '15%' : '5%'}
                                </Typography>
                              </Box>
                            ))}
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>

                    {/* Review List */}
                    {MOCK_REVIEWS.map((review) => (
                      <Card key={review.id} sx={{
                        mb: 2,
                        borderRadius: '8px',
                        border: `1px solid ${axelionColors.borderLight}`,
                        boxShadow: 'none',
                      }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Avatar sx={{ bgcolor: axelionColors.gold }}>
                              {review.clientName.charAt(0)}
                            </Avatar>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: axelionColors.textDark }}>
                                {review.clientName}
                              </Typography>
                              <Typography variant="caption" sx={{ color: axelionColors.textMuted }}>
                                {review.date}
                              </Typography>
                            </Box>
                            <Rating
                              value={review.rating}
                              readOnly
                              size="small"
                              sx={{
                                '& .MuiRating-iconFilled': { color: axelionColors.gold },
                              }}
                            />
                          </Box>
                          <Typography variant="body2" sx={{ color: axelionColors.textSecondary }}>
                            {review.comment}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}

                {/* Tab 2: Portfolio */}
                {currentTab === 2 && (
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: axelionColors.textDark, mb: 2 }}>
                      Портфолио и достижения
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Card sx={{
                          p: 2,
                          textAlign: 'center',
                          bgcolor: axelionColors.accentLight,
                          borderRadius: '8px',
                          border: `1px solid ${axelionColors.borderLight}`,
                          boxShadow: 'none',
                        }}>
                          <CheckCircle sx={{ fontSize: 48, color: axelionColors.gold, mb: 1 }} />
                          <Typography variant="h4" sx={{ fontWeight: 300, color: axelionColors.gold }}>
                            {lawyer.completedConsultations}
                          </Typography>
                          <Typography variant="body2" sx={{ color: axelionColors.textMuted }}>
                            Завершенных консультаций
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Card sx={{
                          p: 2,
                          textAlign: 'center',
                          bgcolor: axelionColors.bgWarm,
                          borderRadius: '8px',
                          border: `1px solid ${axelionColors.borderLight}`,
                          boxShadow: 'none',
                        }}>
                          <ThumbUp sx={{ fontSize: 48, color: axelionColors.bronze, mb: 1 }} />
                          <Typography variant="h4" sx={{ fontWeight: 300, color: axelionColors.bronze }}>
                            {lawyer.successRate}%
                          </Typography>
                          <Typography variant="body2" sx={{ color: axelionColors.textMuted }}>
                            Успешных дел
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Card sx={{
                          p: 2,
                          textAlign: 'center',
                          bgcolor: axelionColors.bgCream,
                          borderRadius: '8px',
                          border: `1px solid ${axelionColors.borderLight}`,
                          boxShadow: 'none',
                        }}>
                          <Star sx={{ fontSize: 48, color: axelionColors.gold, mb: 1 }} />
                          <Typography variant="h4" sx={{ fontWeight: 300, color: axelionColors.gold }}>
                            {lawyer.rating}
                          </Typography>
                          <Typography variant="body2" sx={{ color: axelionColors.textMuted }}>
                            Средний рейтинг
                          </Typography>
                        </Card>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Card sx={{
                          p: 2,
                          textAlign: 'center',
                          bgcolor: axelionColors.bgBeige,
                          borderRadius: '8px',
                          border: `1px solid ${axelionColors.borderLight}`,
                          boxShadow: 'none',
                        }}>
                          <WorkspacePremium sx={{ fontSize: 48, color: axelionColors.goldDark, mb: 1 }} />
                          <Typography variant="h4" sx={{ fontWeight: 300, color: axelionColors.goldDark }}>
                            {lawyer.experience}
                          </Typography>
                          <Typography variant="body2" sx={{ color: axelionColors.textMuted }}>
                            Лет опыта
                          </Typography>
                        </Card>
                      </Grid>
                    </Grid>

                    <Typography variant="body2" sx={{ color: axelionColors.textMuted, mt: 3, textAlign: 'center' }}>
                      Детальная информация о делах доступна только после начала консультации
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default LawyerProfilePage;
